import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

const FormField = ({ field, value, error, touched, onChange, onBlur }) => {
  const [imagePreview, setImagePreview] = useState(null);

  // Gestion de l'aperçu d'image pour les champs de fichier
  useEffect(() => {
    if (field.type === 'file' && value && value instanceof File) {
      // Vérifier si c'est une image
      if (value.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setImagePreview(e.target.result);
        };
        reader.readAsDataURL(value);
      } else {
        setImagePreview(null);
      }
    } else {
      setImagePreview(null);
    }
  }, [value, field.type]);

  const handleFileChange = (e) => {
    onChange(field.name, e.target.files[0] || null);
  };

  // Rendu de l'icône si définie dans le champ
  const renderIcon = () => {
    if (!field.icon) return null;
    
    // Si l'icône est un composant React
    if (typeof field.icon === 'function' || React.isValidElement(field.icon)) {
      return <span className="field-icon">{field.icon}</span>;
    }
    
    // Si l'icône est une classe CSS (comme Font Awesome)
    if (typeof field.icon === 'string') {
      return <i className={`field-icon ${field.icon}`}></i>;
    }
    
    // Si l'icône est un objet avec src (image)
    if (field.icon.src) {
      return (
        <img 
          src={field.icon.src} 
          alt={field.icon.alt || 'Icon'} 
          className="field-icon icon-image"
          style={{ width: field.icon.width || '16px', height: field.icon.height || '16px' }}
        />
      );
    }
    
    return null;
  };

  const renderInput = () => {
    const commonProps = {
      id: field.name,
      name: field.name,
      value: field.type !== 'file' ? (value || '') : undefined,
      onChange: field.type !== 'file'
        ? (e) => onChange(field.name, e.target.value)
        : handleFileChange,
      onBlur: () => onBlur(field.name),
      className: `form-field ${field.type} ${error && touched ? 'error' : ''}`,
      disabled: field.disabled,
      required: field.required
    };

    switch (field.type) {
      
      case 'text':
      case 'email':
      case 'password':
      case 'number':
      case 'tel':
      case 'url':
      case 'date':
      case 'time':
      case 'datetime-local':
      case 'color':
        return (
          <div className="input-with-icon">
            {renderIcon()}
            <input type={field.type} {...commonProps} placeholder={field.placeholder} />
          </div>
        );

      // Champ type "range" avec affichage de la valeur
      case 'range':
        return (
          <div className="range-container">
            {renderIcon()}
            <input 
              type="range" 
              {...commonProps} 
              min={field.min} 
              max={field.max} 
              step={field.step} 
            />
            <span className="range-value">{value}</span>
          </div>
        );

      case 'textarea':
        return (
          <div className="textarea-with-icon">
            {renderIcon()}
            <textarea {...commonProps} rows={field.rows} placeholder={field.placeholder} />
          </div>
        );

      case 'select':
        return (
          <div className="select-with-icon">
            {renderIcon()}
            <select {...commonProps}>
              {field.placeholder && <option value="">{field.placeholder}</option>}
              {field.options.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        );

      // Case à cocher
      case 'checkbox':
        return (
          <div className="checkbox-with-icon">
            <input
              type="checkbox"
              id={field.name}
              name={field.name}
              checked={!!value}
              onChange={(e) => onChange(field.name, e.target.checked)}
              onBlur={() => onBlur(field.name)}
              className={`form-field checkbox ${error && touched ? 'error' : ''}`}
              disabled={field.disabled}
            />
            {renderIcon()}
          </div>
        );
        
      case 'radio':
        return (
          <div className="radio-group">
            {field.options.map(option => (
              <label key={option.value} className="radio-option">
                <input
                  type="radio"
                  name={field.name}
                  value={option.value}
                  checked={value === option.value}
                  onChange={(e) => onChange(field.name, e.target.value)}
                  onBlur={() => onBlur(field.name)}
                />
                {option.icon && (
                  <span className="option-icon">
                    {typeof option.icon === 'string' ? <i className={option.icon}></i> : option.icon}
                  </span>
                )}
                {option.label}
              </label>
            ))}
          </div>
        );

      // Input fichier avec aperçu d'image
      case 'file':
        return (
          <div className="file-input-container">
            <div className="file-input-wrapper">
              {renderIcon()}
              <input
                type="file"
                id={field.name}
                name={field.name}
                onChange={handleFileChange}
                onBlur={() => onBlur(field.name)}
                accept={field.accept}
                className={`form-field file ${error && touched ? 'error' : ''}`}
                disabled={field.disabled}
              />
            </div>
            
            {/* Aperçu de l'image */}
            {imagePreview && (
              <div className="image-preview">
                <img 
                  src={imagePreview} 
                  alt="Aperçu" 
                  className="preview-image"
                  style={{
                    maxWidth: field.previewWidth || '200px',
                    maxHeight: field.previewHeight || '200px'
                  }}
                />
                <button
                  type="button"
                  className="remove-preview"
                  onClick={() => {
                    onChange(field.name, null);
                    setImagePreview(null);
                  }}
                  title="Supprimer l'image"
                >
                  ×
                </button>
              </div>
            )}
            
            {/* Info du fichier sélectionné */}
            {value && !imagePreview && (
              <div className="file-info">
                <span className="file-name">{value.name}</span>
                <span className="file-size">
                  ({Math.round(value.size / 1024)} Ko)
                </span>
                <button
                  type="button"
                  className="remove-file"
                  onClick={() => onChange(field.name, null)}
                  title="Supprimer le fichier"
                >
                  ×
                </button>
              </div>
            )}
          </div>
        );

      // Si le type est inconnu : nada
      default:
        return null;
    }
  };

  // Rendu global avec label, input, messages d'erreur et texte d'aide
  const renderFieldContent = () => {
    if (field.type === 'checkbox') {
      return (
        <label htmlFor={field.name} className="checkbox-label">
          {renderInput()}
          <span className="checkbox-text">
            {field.label}
            {field.required && <span className="required-asterisk">*</span>}
          </span>
        </label>
      );
    }

    return (
      <>
        <label htmlFor={field.name} className="field-label">
          {field.label}
          {field.required && <span className="required-asterisk">*</span>}
        </label>
        {renderInput()}
      </>
    );
  };

  return (
    <div className={`form-field-container ${field.type}-container`}>
      {renderFieldContent()}
      {/* Message d'erreur si le champ a été touché */}
      {error && touched && <div className="error-message">{error}</div>}
      {/* Petit texte d'aide, genre "Entrez votre email pro…" */}
      {field.helpText && <div className="help-text">{field.helpText}</div>}
    </div>
  );
};

FormField.propTypes = {
  field: PropTypes.object.isRequired,
  value: PropTypes.any,
  error: PropTypes.string,
  touched: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired
};

export default FormField;